import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { BuyerGroupFormData } from '../../../../Types/AdvanceSearchResultsTypes';
import { actions } from '../../../../Slice/AdvanceSearchSlice';
import {
  selectAdvanceSearchBrmForm,
  selectAdvanceSearchMemberOptions,
  selectAdvanceSearchMemberLoading,
  selectAdvanceSearchMemberHasMore,
  selectAdvanceSearchMemberOffset,
  selectAdvanceSearchMemberOptionsLoaded,
  selectAdvanceSearchMemberSearchTerm,
  selectAdvanceSearchSharedByOptions,
  selectAdvanceSearchSharedByLoading,
  selectAdvanceSearchSharedByHasMore,
  selectAdvanceSearchSharedByPage,
  selectAdvanceSearchSharedByOptionsLoaded,
  selectAdvanceSearchSharedBySearchTerm,
} from '../../../../Selector/AdvanceSearchSelector';
import { FormField } from './FormField';

const FormContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface BrmFormProps {
  onValidityChange?: (isValid: boolean) => void;
  onFormDataChange?: (data: BuyerGroupFormData) => void;
  isLoading?: boolean;
  brmSizes?: string[];
}

export const BrmForm: React.FC<BrmFormProps> = ({
  onValidityChange,
  onFormDataChange,
  isLoading,
  brmSizes,
}) => {
  const dispatch = useDispatch();
  const formData = useSelector(selectAdvanceSearchBrmForm);

  const memberOptions = useSelector(selectAdvanceSearchMemberOptions);
  const memberLoading = useSelector(selectAdvanceSearchMemberLoading);
  const memberHasMore = useSelector(selectAdvanceSearchMemberHasMore);
  const memberOffset = useSelector(selectAdvanceSearchMemberOffset);
  const memberOptionsLoaded = useSelector(selectAdvanceSearchMemberOptionsLoaded);
  const sharedByOptions = useSelector(selectAdvanceSearchSharedByOptions);
  const sharedByLoading = useSelector(selectAdvanceSearchSharedByLoading);
  const sharedByHasMore = useSelector(selectAdvanceSearchSharedByHasMore);
  const sharedByPage = useSelector(selectAdvanceSearchSharedByPage);
  const sharedByOptionsLoaded = useSelector(selectAdvanceSearchSharedByOptionsLoaded);
  const memberSearchTerm = useSelector(selectAdvanceSearchMemberSearchTerm);
  const sharedBySearchTerm = useSelector(selectAdvanceSearchSharedBySearchTerm);
  
  const [sizeId, setSizeId] = useState(0);

  const updateField = (field: keyof BuyerGroupFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    dispatch(actions.setAdvanceSearchBrmForm(newData));
  };

  const brmSizeOptions = useMemo(
    () => (brmSizes ?? []).map((label, i) => ({ id: i + 1, label })),
    [brmSizes],
  );

  const handleSizeChange = (id: number) => {
    setSizeId(id);
    const selectedLabel = brmSizeOptions.find((option) => option.id === id)?.label ?? '';
    updateField('size', selectedLabel);
  };

  const handleMemberOpen = () => {
    if (!memberOptionsLoaded && !memberLoading) {
      dispatch(actions.getAdvanceSearchMembers({ offset: 0 }));
    }
  };

  const handleSharedByOpen = () => {
    if (!sharedByOptionsLoaded && !sharedByLoading) {
      dispatch(actions.getAdvanceSearchSharedBy({ page: 1 }));
    }
  };

  const loadMoreMembers = () => {
    if (!memberLoading && memberHasMore) {
      dispatch(actions.getAdvanceSearchMembers({
        offset: memberOffset,
        searchTerm: memberSearchTerm || undefined,
      }));
    }
  };

  const loadMoreSharedBy = () => {
    if (!sharedByLoading && sharedByHasMore) {
      dispatch(actions.getAdvanceSearchSharedBy({
        page: sharedByPage,
        query: sharedBySearchTerm || undefined,
      }));
    }
  };

  const handleMemberSearch = (term: string) => {
    const searchTerm = term.trim();
    dispatch(actions.setMemberSearchTerm(searchTerm));
    dispatch(actions.getAdvanceSearchMembers({
      offset: 0,
      searchTerm: searchTerm || undefined,
    }));
  };

  const handleSharedBySearch = (term: string) => {
    const searchTerm = term.trim();
    dispatch(actions.setSharedBySearchTerm(searchTerm));
    dispatch(actions.getAdvanceSearchSharedBy({
      page: 1,
      query: searchTerm || undefined,
    }));
  };

  useEffect(() => {
    const isValid = Object.values(formData).some((val) => {
      if (typeof val === 'number') {
        return val !== 0;
      }
      return String(val).trim() !== '';
    });
    onValidityChange?.(isValid);
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange, onValidityChange]);

  useEffect(() => {
    const matchedId = brmSizeOptions.find((option) => option.label === formData.size)?.id ?? 0;
    setSizeId(matchedId);
  }, [brmSizeOptions, formData.size]);

  return (
    <FormContainer>
      <FormField
        label="Name"
        value={formData.name}
        onChange={(val) => updateField('name', val)}
        placeholder="Enter buyer group name"
      />
      <FormField
        label="Member"
        type="dropdown"
        value={formData.member}
        onChange={(val) => updateField('member', val)}
        placeholder="Select people name"
        options={memberOptions}
        hasSearch
        hasMore={!!memberHasMore}
        loadMore={loadMoreMembers}
        isLoading={memberLoading}
        isCardOption
        useRemoteSearch
        onSearchSubmit={handleMemberSearch}
        onDropdownOpen={handleMemberOpen}
        initialSearchTerm={memberSearchTerm}
      />
      <FormField
        label="Size"
        type="dropdown"
        value={sizeId}
        options={brmSizeOptions}
        onChange={(val) => handleSizeChange(val as number)}
        placeholder="Select buyer group size"
      />
      <FormField
        label="Shared By"
        type="dropdown"
        value={formData.sharedBy}
        onChange={(val) => updateField('sharedBy', val)}
        placeholder="Select shared people"
        options={sharedByOptions}
        hasSearch
        hasMore={!!sharedByHasMore}
        loadMore={loadMoreSharedBy}
        isLoading={sharedByLoading}
        useRemoteSearch
        onSearchSubmit={handleSharedBySearch}
        onDropdownOpen={handleSharedByOpen}
        initialSearchTerm={sharedBySearchTerm}
      />
    </FormContainer>
  );
};
